import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Search, RotateCcw } from "lucide-react";
import { DashboardCard } from "@/components/DashboardCard";

const FILTERS = [
  { key: "all", label: "ALL" },
  { key: "grafana", label: "GRAFANA" },
  { key: "exor", label: "EXOR" },
  { key: "scada", label: "SCADA" },
  { key: "other", label: "CUSTOM" },
];

function Skeleton() {
  return (
    <div className="animate-pulse border border-hairline bg-white/60 p-6">
      <div className="h-5 w-24 bg-surface2" />
      <div className="mt-4 h-6 w-3/4 bg-surface2" />
      <div className="mt-2 h-4 w-1/2 bg-surface2" />
      <div className="mt-6 h-16 border-t border-hairline pt-4">
        <div className="h-4 w-full bg-surface2" />
        <div className="mt-2 h-4 w-2/3 bg-surface2" />
      </div>
      <div className="mt-6 h-11 w-full bg-surface2" />
    </div>
  );
}

export function DashboardGrid({ cards, loading, onAdd, onEdit }) {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");

  const visible = useMemo(() => {
    let list = cards;
    if (filter !== "all") list = list.filter((c) => c.type === filter);
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((c) =>
        [c.name, c.plant, c.url].some((v) =>
          (v || "").toLowerCase().includes(q)
        )
      );
    }
    return list;
  }, [cards, query, filter]);

  const pad = (n) => String(n).padStart(2, "0");

  return (
    <section
      id="deck"
      className="relative mx-auto max-w-7xl scroll-mt-16 px-5 py-20 sm:px-8 md:py-28"
    >
      <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.6 }}
        >
          <p className="flex items-center gap-2.5 font-mono text-[11px] tracking-[0.25em] text-ink/60">
            <span className="h-2 w-2 bg-signal" />
            THE DECK
          </p>
          <h2 className="mt-4 font-display text-4xl font-extrabold tracking-[-0.02em] md:text-5xl">
            PLANT DASHBOARDS
            <span className="ml-3 align-top font-mono text-base font-medium text-ink/40">
              ({pad(cards.length)})
            </span>
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="flex flex-col gap-3 sm:flex-row sm:items-center"
        >
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" />
            <input
              data-testid="search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, plant or URL"
              className="w-full border border-hairline bg-white py-2.5 pl-9 pr-3 font-mono text-xs transition-colors placeholder:text-ink/35 focus:border-ink focus:outline-none sm:w-72"
            />
          </div>
          <button
            data-testid="deck-add-button"
            onClick={onAdd}
            className="inline-flex items-center justify-center gap-2 bg-ink px-5 py-2.5 font-mono text-[11px] tracking-[0.2em] text-paper transition-colors duration-300 hover:bg-signal"
          >
            <Plus className="h-3.5 w-3.5" />
            ADD DASHBOARD
          </button>
        </motion.div>
      </div>

      <div
        className="mt-8 flex flex-wrap gap-2"
        data-testid="filter-chips"
      >
        {FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={`filter-chip-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`border px-3.5 py-2 font-mono text-[11px] tracking-[0.15em] transition-colors duration-200 ${
              filter === f.key
                ? "border-ink bg-ink text-paper"
                : "border-hairline text-ink/60 hover:border-ink hover:text-ink"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8 lg:grid-cols-3">
          <Skeleton />
          <Skeleton />
          <Skeleton />
        </div>
      ) : visible.length > 0 ? (
        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8 lg:grid-cols-3">
          {visible.map((card, i) => (
            <DashboardCard key={card.id} card={card} index={i} onEdit={onEdit} />
          ))}
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          data-testid="empty-state"
          className="mt-12 border border-dashed border-ink/30 bg-white/50 px-8 py-20 text-center"
        >
          <p className="font-display text-2xl font-bold tracking-tight">
            {cards.length === 0 ? "The deck is empty" : "No matches found"}
          </p>
          <p className="mx-auto mt-3 max-w-sm text-sm text-ink/55">
            {cards.length === 0
              ? "Add your first plant dashboard or remote SCADA view as a card, then launch it from here any time."
              : "Try a different search term or clear the active filter."}
          </p>
          {cards.length === 0 ? (
            <button
              data-testid="empty-add-button"
              onClick={onAdd}
              className="mt-8 inline-flex items-center gap-2 bg-ink px-6 py-3 font-mono text-[11px] tracking-[0.2em] text-paper transition-colors duration-300 hover:bg-signal"
            >
              <Plus className="h-3.5 w-3.5" />
              ADD FIRST DASHBOARD
            </button>
          ) : (
            <button
              data-testid="reset-filters-button"
              onClick={() => {
                setQuery("");
                setFilter("all");
              }}
              className="mt-8 inline-flex items-center gap-2 border border-ink/25 px-6 py-3 font-mono text-[11px] tracking-[0.2em] transition-colors duration-300 hover:border-ink"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              RESET SEARCH
            </button>
          )}
        </motion.div>
      )}
    </section>
  );
}
